/**
 * Accessibility patches for the Alia popup (app embed).
 *
 * Alia renders its popup with React into a <div id="alia-root-..."> it
 * appends to <body>, so theme markup can't reach it; this patches the popup's
 * DOM as Alia renders each step. Found by the ONBRAND sweep on /cart
 * (2026-10-06), where the "Claim A Mystery Discount" quiz opens over the page
 * for every new visitor; the same popup sits on the other tracked pages.
 *
 * 1. The close control's X is an unnamed <svg> (IBM svg_graphics_labelled,
 *    WCAG 1.1.1). The control names itself ("Close popup"), so any svg in the
 *    popup with no name of its own is hidden from assistive tech.
 *
 * 2. The quiz answers and the "Continue" button copy the theme's orange pill
 *    but print a 15-17px white label on it: 3.0:1, which passes only as large
 *    text (axe color-contrast, WCAG 1.4.3). JIYU's rule for that pill (TASKS
 *    #184) is a 19px bold label, so a bold label in that band is set to 19px.
 *    Ink text would fail on hover, where Alia turns the button green. The
 *    label is measured each time, so this does nothing once Alia's colours or
 *    sizes pass, and a label that would wrap at 19px is left alone.
 *
 * Whether a label fits can only be judged in Alia's own font. Alia adds its
 * font faces with the FontFace API after the popup renders, which fires no
 * event on document.fonts, so each root is re-checked on a short poll until
 * its fonts arrive or GRACE_MS passes.
 *
 * The edits are attributes only: React updates an attribute only when its own
 * prop changes, so they survive re-renders, and an attribute change can't
 * re-trigger the childList observers. Remove this file with the app.
 */
(function () {
  'use strict';

  if (window.jiyuAliaA11y) return;
  window.jiyuAliaA11y = true;

  var LARGE_BOLD_PX = 19; // the theme pill's label size; WCAG large bold text starts at 18.66px
  var MARK = 'data-jiyu-a11y-size';
  var GRACE_MS = 10000;
  var POLL_MS = 500;

  function parseColor(value) {
    var m = /rgba?\(([^)]+)\)/.exec(value || '');
    if (!m) return null;
    var p = m[1].split(/[\s,\/]+/).filter(Boolean).map(parseFloat);
    return { r: p[0], g: p[1], b: p[2], a: p.length > 3 ? p[3] : 1 };
  }

  function luminance(c) {
    function channel(v) {
      v /= 255;
      return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    }
    return 0.2126 * channel(c.r) + 0.7152 * channel(c.g) + 0.0722 * channel(c.b);
  }

  function contrast(a, b) {
    var la = luminance(a);
    var lb = luminance(b);
    return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
  }

  function unquote(name) {
    return name.trim().replace(/^["']|["']$/g, '');
  }

  // true once the label's own font has loaded (or failed). A family with no
  // face registered is a system font, or Alia's not added yet: settled only
  // once the grace period is over.
  function fontSettled(control, graceOver) {
    var style = getComputedStyle(control);
    var family = unquote(style.fontFamily.split(',')[0]);
    var registered = false;
    if (!document.fonts) return true;
    document.fonts.forEach(function (face) {
      if (unquote(face.family) === family) registered = true;
    });
    if (!registered) return graceOver;
    try {
      return document.fonts.check(style.fontStyle + ' ' + style.fontWeight + ' ' + LARGE_BOLD_PX + 'px ' + style.fontFamily);
    } catch (e) {
      return true;
    }
  }

  // true when the label runs onto a second line (Alia clamps its buttons to one)
  function wraps(control) {
    var range = document.createRange();
    range.selectNodeContents(control);
    var tops = [];
    Array.prototype.forEach.call(range.getClientRects(), function (rect) {
      var top = Math.round(rect.top);
      if (rect.width && tops.indexOf(top) === -1) tops.push(top);
    });
    return tops.length > 1 || control.scrollWidth > control.clientWidth + 1;
  }

  function fits(control, graceOver) {
    return !fontSettled(control, graceOver) || !wraps(control);
  }

  function shrink(control) {
    control.style.fontSize = '';
    control.removeAttribute(MARK);
  }

  function enlargeLabel(control, graceOver) {
    if (control.hasAttribute(MARK)) {
      if (!fits(control, graceOver)) shrink(control);
      return;
    }
    if (control.disabled || !control.textContent.trim()) return;
    if (!control.checkVisibility || !control.checkVisibility()) return;

    var style = getComputedStyle(control);
    var fg = parseColor(style.color);
    var bg = parseColor(style.backgroundColor);
    // only an opaque label on an opaque button can be judged here; Alia
    // inserts each button before its stylesheet, so the first look sees none
    if (!fg || !bg || fg.a < 1 || bg.a < 1) return;

    var size = parseFloat(style.fontSize);
    var bold = parseInt(style.fontWeight, 10) >= 700;
    var ratio = contrast(fg, bg);
    if (!bold || size >= LARGE_BOLD_PX || ratio >= 4.5 || ratio < 3) return;

    control.style.fontSize = LARGE_BOLD_PX + 'px';
    control.setAttribute(MARK, '');
    if (!fits(control, graceOver)) shrink(control);
  }

  function patch(root, graceOver) {
    root.querySelectorAll('svg:not([aria-hidden])').forEach(function (svg) {
      if (svg.getAttribute('aria-label') || svg.getAttribute('aria-labelledby') || svg.querySelector('title')) return;
      svg.setAttribute('aria-hidden', 'true');
      svg.setAttribute('focusable', 'false');
    });
    root.querySelectorAll('button, [role="button"]').forEach(function (control) {
      enlargeLabel(control, graceOver);
    });
  }

  function watch(root) {
    if (root.jiyuAliaA11y) return;
    root.jiyuAliaA11y = true;

    var started = Date.now();
    var graceOver = false;
    var run = function () {
      if (root.isConnected) patch(root, graceOver);
    };

    run();
    new MutationObserver(run).observe(root, { childList: true, subtree: true });
    if (document.fonts && document.fonts.addEventListener) {
      document.fonts.addEventListener('loadingdone', run);
    }
    var timer = setInterval(function () {
      graceOver = Date.now() - started >= GRACE_MS;
      run();
      if (graceOver || !root.isConnected) clearInterval(timer);
    }, POLL_MS);
  }

  function scan() {
    for (var i = 0; i < document.body.children.length; i++) {
      var el = document.body.children[i];
      // getAttribute, not el.id: the Meta Pixel adds a <form> holding
      // <input name="id"> to <body>, and on it form.id is that input
      var id = el.getAttribute('id') || '';
      if (id.indexOf('alia-root') === 0) watch(el);
    }
  }

  scan();
  new MutationObserver(scan).observe(document.body, { childList: true });
})();
